import mustache from "mustache";
import { BaseRequest } from "../../../../base/request/BaseRequest";
import { BaseResponse } from "../../../../base/response/BaseResponse";
import { FormField } from "../../../base/form/FormField";
import { SelectConfig } from "../../../definitions/form/inputs/config/SelectConfig";
import { SelectValidation } from "../../../definitions/form/inputs/validation/SelectValidation";
import { IInputSelectField } from "../../../definitions/form/inputs/IInputSelectField";
import template from "./template.html";

mustache.parse(template);

/** Campo `<select>` del sistema nuevo. El propio campo le pide las opciones a su
 *  `service` en `connect()`, las filtra (si hay `filter`), las pinta (con la
 *  opción vacía si se configuró) y selecciona el valor actual del request. El
 *  value de cada opción es `item.id`; la opción vacía escribe "" en el request. */
export class InputSelectField<
    TRequest extends BaseRequest,
    TResponse extends BaseResponse & { id: string },
  >
  extends FormField<SelectConfig<TRequest, TResponse>>
  implements IInputSelectField
{
  private readonly _request: TRequest;
  private readonly _validation: SelectValidation;

  constructor(
    request: TRequest,
    config: SelectConfig<TRequest, TResponse>,
    validation: SelectValidation,
  ) {
    super(config);
    this._request = request;
    this._validation = validation;
  }

  private _onValueChange?: () => void;

  /** Valor (id) actualmente seleccionado (desde el request, fuente de verdad). */
  getValue(): string {
    return this._currentValue();
  }

  setOnChange(handler: () => void): void {
    this._onValueChange = handler;
  }

  getTemplate(): string {
    return template;
  }

  getData(): unknown {
    const isRequired = this._validation.isRequired ?? false;
    return {
      label: `${this._config.label}${isRequired ? " *" : ""}`,
      description: this._config.description ?? "",
      id: this._formFieldId,
    };
  }

  connect(): void {
    this._select = document.getElementById(
      `select-${this._formFieldId}`,
    ) as HTMLSelectElement;
    this._error = document.getElementById(`error-${this._formFieldId}`);
    this._select?.addEventListener("change", this._onChange);
    void this._loadOptions();
  }

  doDispose(): void {
    this._select?.removeEventListener("change", this._onChange);
  }

  getError(): string | null {
    if (this._validation.isRequired && this._currentValue().length === 0)
      return `Seleccione ${this._config.label}`;
    return null;
  }

  showError(): void {
    const error = this.getError();
    if (this._error) this._error.innerHTML = error ?? "";
    this._select?.classList.toggle("error", error !== null);
  }

  focus(): void {
    this._select?.focus();
  }

  disabled(): void {
    if (this._select) this._select.disabled = true;
  }

  enabled(): void {
    if (this._select) this._select.disabled = false;
  }

  /** Recarga las opciones (re-aplica el filtro y re-selecciona el valor actual).
   *  Útil cuando cambia una dependencia (select dependiente). */
  reload(): Promise<void> {
    return this._loadOptions();
  }

  private _select: HTMLSelectElement | null = null;
  private _error: HTMLElement | null = null;

  /** Le pide las opciones al servicio, las filtra, las pinta (con la vacía si
   *  aplica) y selecciona el valor actual del request. El campo pudo disponerse
   *  mientras cargaba. */
  private async _loadOptions(): Promise<void> {
    const items = await this._config.service.getListAsync();
    if (!this._select?.isConnected) return;

    const fragment = document.createDocumentFragment();
    if (this._config.emptyLabel != null) {
      fragment.appendChild(this._createOption("", this._config.emptyLabel));
    }
    for (const item of items) {
      if (this._config.filter && !this._config.filter(item, this._request))
        continue;
      fragment.appendChild(
        this._createOption(item.id, this._config.getLabel(item)),
      );
    }
    this._select.replaceChildren(fragment);
    this._select.value = this._currentValue();
  }

  private _createOption(value: string, label: string): HTMLOptionElement {
    const el = document.createElement("option");
    el.value = value;
    el.textContent = label;
    return el;
  }

  private _currentValue(): string {
    return this._config.get(this._request) ?? "";
  }

  private _onChange = (): void => {
    if (!this._select) return;
    // La opción vacía ("") se normaliza a null para el request.
    this._config.set(this._request, this._select.value || null);
    this.showError();
    this._onValueChange?.();
  };
}
